import { useState } from 'react'
import { ArrowRight, BookOpen, Sparkles } from 'lucide-react'
import { trackUpgradeCtaClicked } from '@/shared/utils/analytics'
import PaywallModal from './PaywallModal'
import type { PaywallTriggerLocation } from '../types'

interface UpgradeCTAProps {
  location: PaywallTriggerLocation
  title?: string
  description?: string
  buttonText?: string
  variant?: 'default' | 'compact'
}

/**
 * Upgrade CTA component for use after test results
 * Prompts users to improve with PYQs
 */
export default function UpgradeCTA({
  location,
  title = 'Improve with PYQs',
  description = 'Practice with 20+ years of NEET questions to strengthen weak areas and boost your score.',
  buttonText = 'Unlock All PYQs',
  variant = 'default',
}: UpgradeCTAProps) {
  const [showPaywall, setShowPaywall] = useState(false)

  const handleClick = () => {
    trackUpgradeCtaClicked(location)
    setShowPaywall(true)
  }

  if (variant === 'compact') {
    return (
      <>
        <button
          onClick={handleClick}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-sm"
        >
          <Sparkles className="w-4 h-4" />
          {buttonText}
          <ArrowRight className="w-4 h-4" />
        </button>

        <PaywallModal
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          triggerLocation={location}
        />
      </>
    )
  }

  return (
    <>
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {title}
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              {description}
            </p>
            <button
              onClick={handleClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              {buttonText}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        triggerLocation={location}
      />
    </>
  )
}
